import { useCallback, useEffect, useRef, useState } from 'react';
import { api, NetworkError } from '../lib/api';
import type { Message } from '../components/conversations/types';

/** CONV2 (c): how often the open thread refreshes on its own while the page is visible. */
export const THREAD_REFRESH_MS = 10_000;

/**
 * A message time as epoch ms. The server sends numbers since CONV2 (the 142nd); GoHighLevel's own shape is TEXT
 * ("2026-10-15T18:21:02.394Z"), and the old `a - b` sort on two texts was NaN - nothing moved, so the thread kept
 * GoHighLevel's newest-first order and read newest on TOP. Read either, so the order never depends on one layer.
 */
const timeOf = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isNaN(t) ? 0 : t;
  }
  return 0;
};
const oldestFirst = (list: Message[]): Message[] => list.slice().sort((a, b) => timeOf(a.date_added) - timeOf(b.date_added));

/** A bubble that lives only on this screen until GoHighLevel's thread has it (sending) or for good (failed, or not confirmed). */
const isLocalBubble = (m: Message) =>
  m.id.startsWith('pending-') && (m.status === 'pending' || m.status === 'failed' || m.status === 'unconfirmed');

// Owns a single conversation thread: live message fetch, refresh on its own, optimistic send.
export function useThread(conversationId: string | null, channel: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  // ⛔ Every load is numbered and only the LATEST may land: a slow answer for the thread he left must never replace the
  // one he opened (a ref shared by every load is not a stale-answer guard - the 104th's ruling).
  const loadSeq = useRef(0);
  const inFlight = useRef(false);

  // `silent`: a refresh in the background - no "Loading", the thread stays on screen, a failure is quiet (the next
  // refresh tries again) and this screen's own bubbles are kept. `force`: run even if another load is on its way.
  const load = useCallback(async ({ silent = false, force = false }: { silent?: boolean; force?: boolean } = {}) => {
    if (!conversationId) {
      loadSeq.current += 1;
      setMessages([]);
      return;
    }
    if (silent && !force && inFlight.current) return;
    const seq = ++loadSeq.current;
    inFlight.current = true;
    if (!silent) {
      setMessages([]);
      setLoading(true);
      setError(null);
    }
    try {
      const res = await api.getConversationMessages(conversationId);
      if (seq !== loadSeq.current) return;
      const fetched = oldestFirst(res.messages || []);
      if (silent) {
        // In time order: a failed text keeps the place it was sent, and a newer text arrives below it.
        setMessages((prev) => oldestFirst([...fetched, ...prev.filter(isLocalBubble)]));
        setError(null);
      } else {
        setMessages(fetched);
      }
    } catch (e: any) {
      if (seq !== loadSeq.current) return;
      if (!silent) setError(e?.message || 'Couldn’t load messages from GoHighLevel');
    } finally {
      if (seq === loadSeq.current) {
        inFlight.current = false;
        setLoading(false);
      }
    }
  }, [conversationId]);

  useEffect(() => {
    load();
  }, [load]);

  // CONV2 (c): new messages appear on their own - every THREAD_REFRESH_MS while the page is visible, and at once when
  // he comes back to it (a hidden page asks nothing).
  useEffect(() => {
    if (!conversationId) return undefined;
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    };
    const timer = window.setInterval(refreshIfVisible, THREAD_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshIfVisible);
    window.addEventListener('focus', refreshIfVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshIfVisible);
      window.removeEventListener('focus', refreshIfVisible);
    };
  }, [conversationId, load]);

  // Send is the ONLY outbound path. Optimistically append, then reconcile with GHL.
  // `sendChannel` lets the composer's channel picker override the thread's channel.
  const send = useCallback(
    async (message: string, subject?: string, sendChannel?: string, fromNumber?: string) => {
      if (!conversationId || !message.trim()) return;
      const ch = sendChannel || channel;
      const key =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `k-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
      const optimistic: Message = {
        id: `pending-${key}`,
        body: message,
        direction: 'outbound',
        date_added: Date.now(),
        message_type: ch,
        status: 'pending',
        ...(subject ? { subject } : {}), // an email on its way shows the subject he wrote (I3)
      };
      setMessages((prev) => [...prev, optimistic]);
      setSending(true);
      try {
        await api.sendConversationMessage(conversationId, { channel: ch, message, subject, idempotencyKey: key, fromNumber });
        // Sent: the next answer from GoHighLevel carries it (a refresh drops this screen's copy once it is 'sent').
        setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? { ...m, status: 'sent' } : m)));
        await load({ silent: true, force: true }); // reconcile with GHL truth
      } catch (e: any) {
        // CONV2 (a): the server's reason stays on the bubble - on 10-15 it said "This contact has no phone number for
        // SMS" and the screen showed only "Failed to send".
        // I4: the server's 504 means it MAY have gone out (no clear answer from GoHighLevel) - "not confirmed", never
        // "failed", which invites a second text to the same lead.
        // The 143rd's reviewer: a NetworkError is the same "no clear answer" one hop nearer him - the server may have
        // sent it and the answer been lost on the way back to his phone. Nothing here can tell that from a request that
        // never left, so it is "not confirmed" too.
        const lostAnswer = e instanceof NetworkError;
        const status = lostAnswer || e?.status === 504 ? 'unconfirmed' : 'failed';
        const error = lostAnswer
          ? `The connection dropped before the server answered - this ${/mail/i.test(ch) ? 'email' : 'text'} may have gone out. Wait for the thread to refresh before sending it again.`
          : e?.message || 'Could not send';
        setMessages((prev) =>
          prev.map((m) => (m.id === optimistic.id ? { ...m, status, error } : m)));
        throw e;
      } finally {
        setSending(false);
      }
    },
    [conversationId, channel, load]
  );

  // AI draft — returns text for the composer to edit. Never sends.
  const draft = useCallback(
    async (draftChannel: string, todoTaskId?: string): Promise<string> => {
      if (!conversationId) return '';
      // TODO1: with a to-do, the server drafts what that to-do says the message must do.
      const res = await api.draftConversationReply(conversationId, draftChannel, todoTaskId);
      return res.draft || '';
    },
    [conversationId]
  );

  // The Apex line this SMS thread is on (latest message): inbound -> to, outbound -> from.
  let sendingNumber: string | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    const n = m.direction === 'outbound' ? m.from_number : m.to_number;
    if (n) { sendingNumber = n; break; }
  }

  return { messages, loading, error, sending, send, draft, sendingNumber, reload: load };
}
